import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { appConfig } from "../configs/tenantConfig";

import navbarEn from "./locales/en/navbar.json";
import dashboardEn from "./locales/en/dashboard.json";
import sidebarEn from "./locales/en/sidebar.json";
import designsEn from "./locales/en/designs.json";
import workspaceEn from "./locales/en/workspace.json";
import DraftsEn from "./locales/en/drafts.json";
import HistoryEn from "./locales/en/history.json";
import SettingsEn from "./locales/en/settings.json";
import verificationApprovalsEn from "./locales/en/verificationApprovals.json";
import TenantEn from "./locales/en/tenant.json";
import verifierApprovalEn from "./locales/en/verifierApproval.json";
import auditLogsEn from "./locales/en/auditLogs.json";
import authPagesEn from "./locales/en/authPages.json";
import profileEn from "./locales/en/profile.json";
import resetpasswordEn from "./locales/en/resetpassword.json";
import certificateOnboardingEn from "./locales/en/certificateOnboarding.json";
import financeEn from "./locales/en/finance.json";
import holdersEn from "./locales/en/holders.json";
import htmlDesignerEn from "./locales/en/htmlDesigner.json";
import walletOfferEn from "./locales/en/walletOffer.json";
import onboardingEn from "./locales/en/onboarding.json";

import navbarPt from "./locales/pt/navbar.json";
import dashboardPt from "./locales/pt/dashboard.json";
import sidebarPt from "./locales/pt/sidebar.json";
import designsPt from "./locales/pt/designs.json";
import workspacePt from "./locales/pt/workspace.json";
import DraftsPt from "./locales/pt/drafts.json";
import HistoryPt from "./locales/pt/history.json";
import SettingsPt from "./locales/pt/settings.json";
import verificationApprovalsPt from "./locales/pt/verificationApprovals.json";
import TenantPt from "./locales/pt/tenant.json";
import verifierApprovalPt from "./locales/pt/verifierApproval.json";
import auditLogsPt from "./locales/pt/auditLogs.json";
import authPagesPt from "./locales/pt/authPages.json";
import profilePt from "./locales/pt/profile.json";
import resetpasswordPt from "./locales/pt/resetpassword.json";
import certificateOnboardingPt from "./locales/pt/certificateOnboarding.json";
import financePt from "./locales/pt/finance.json";
import holdersPt from "./locales/pt/holders.json";
import htmlDesignerPt from "./locales/pt/htmlDesigner.json";
import walletOfferPt from "./locales/pt/walletOffer.json";
import onboardingPt from "./locales/pt/onboarding.json";

const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: "appStoredLanguage",
  lookup() {
    return (
      localStorage.getItem("selectedLanguage") ||
      localStorage.getItem("i18nextLng") ||
      undefined
    );
  },
});
languageDetector.addDetector({
  name: "tenantPreferredLocale",
  lookup() {
    return appConfig.preferredLocale || undefined;
  },
});

const isPlainObject = (value) =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const applyLocaleOverrides = (base, overrides) => {
  if (!isPlainObject(overrides)) return base;

  const merge = (baseValue, overrideValue) => {
    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      const merged = { ...baseValue };
      Object.keys(overrideValue).forEach((key) => {
        merged[key] = merge(baseValue[key], overrideValue[key]);
      });
      return merged;
    }
    return overrideValue === undefined ? baseValue : overrideValue;
  };

  return merge(base, overrides);
};

const baseResources = {
  en: {
    navbar: navbarEn,
    dashboard: dashboardEn,
    sidebar: sidebarEn,
    designs: designsEn,
    workspace: workspaceEn,
    drafts: DraftsEn,
    history: HistoryEn,
    settings: SettingsEn,
    verificationApprovals: verificationApprovalsEn,
    tenant: TenantEn,
    verifierApproval: verifierApprovalEn,
    auditLogs: auditLogsEn,
    authPages: authPagesEn,
    profile: profileEn,
    resetpassword: resetpasswordEn,
    certificateOnboarding: certificateOnboardingEn,
    finance: financeEn,
    holders: holdersEn,
    htmlDesigner: htmlDesignerEn,
    walletOffer: walletOfferEn,
    onboarding: onboardingEn,
  },
  pt: {
    navbar: navbarPt,
    dashboard: dashboardPt,
    sidebar: sidebarPt,
    designs: designsPt,
    workspace: workspacePt,
    drafts: DraftsPt,
    history: HistoryPt,
    settings: SettingsPt,
    verificationApprovals: verificationApprovalsPt,
    tenant: TenantPt,
    verifierApproval: verifierApprovalPt,
    auditLogs: auditLogsPt,
    authPages: authPagesPt,
    profile: profilePt,
    resetpassword: resetpasswordPt,
    certificateOnboarding: certificateOnboardingPt,
    finance: financePt,
    holders: holdersPt,
    htmlDesigner: htmlDesignerPt,
    walletOffer: walletOfferPt,
    onboarding: onboardingPt,
  },
};

const resources = applyLocaleOverrides(
  baseResources,
  appConfig.localeOverrides,
);

i18next
  .use(initReactI18next)
  .use(languageDetector)
  .use(Backend)
  .init({
    resources,
    debug: process.env.NODE_ENV === "development",
    fallbackLng: appConfig.preferredLocale || "en",

    ns: [
      "navbar",
      "dashboard",
      "sidebar",
      "designs",
      "workspace",
      "drafts",
      "history",
      "settings",
      "verificationApprovals",
      "tenant",
      "verifierApproval",
      "auditLogs",
      "authPages",
      "profile",
      "resetpassword",
      "certificateOnboarding",
      "finance",
      "holders",
      "htmlDesigner",
      "walletOffer",
    ],
    defaultNS: "navbar",

    detection: {
      order: ["appStoredLanguage", "tenantPreferredLocale", "navigator"],
      caches: ["localStorage"],
    },

    interpolation: {
      escapeValue: false,
    },

    react: {
      useSuspense: false,
    },
  });

export default i18next;
